import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Send, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Reveal, Eyebrow } from '../components/Shared';
import { AnimatedHeading } from '../components/Shared';
import { DiscoveryField } from '../components/discovery/DiscoveryField';
import { discoverySections, emptyAnswers } from '../data/discoveryQuestions';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.port === '3000'
    ? 'http://localhost:5000'
    : '');
const API = `${BACKEND}/api/discovery`;
const REVIEW = discoverySections.length;

const validate = (section, answers) => {
  const errors = {};
  section.fields.forEach(field => {
    const value = answers[field.key];
    if (field.required && !String(value).trim()) errors[field.key] = `${field.label} is required.`;
    else if (field.key === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) errors[field.key] = 'Please enter a valid email address.';
  });
  return errors;
};

const display = value => (Array.isArray(value) ? value.join(', ') : value);

export default function AiDiscovery() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(emptyAnswers);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState('');
  const [done, setDone] = useState(false);

  const section = discoverySections[step];
  const progress = Math.round(((step + (done ? 1 : 0)) / REVIEW) * 100);

  const set = (key, value) => { setAnswers(prev => ({ ...prev, [key]: value })); setErrors(prev => ({ ...prev, [key]: undefined })); };
  const goto = target => { setStep(target); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const next = () => {
    if (step < REVIEW) {
      const found = validate(discoverySections[step], answers);
      if (Object.keys(found).length) { setErrors(found); return; }
    }
    goto(Math.min(step + 1, REVIEW));
  };

  const submit = async () => {
    setSending(true); setFailure('');
    const payload = Object.fromEntries(Object.entries(answers).filter(([, v]) => (Array.isArray(v) ? v.length : String(v).trim())));
    try {
      const res = await fetch(`${API}/submissions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers: payload }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Submission failed');
      setDone(true); window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setFailure(typeof e.message === 'string' ? e.message : 'Something went wrong. Please try again.');
    } finally { setSending(false); }
  };

  if (done) return <main id="main-content" className="container discovery-page" data-testid="discovery-page">
    <div className="discovery-shell" style={{ paddingTop: 150 }}>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="discovery-card glass discovery-done" role="status" aria-live="polite" data-testid="discovery-confirmation">
        <span className="success-check"><Check size={32} /></span>
        <h2 data-testid="discovery-confirmation-heading">Thanks — we’ll follow up shortly.</h2>
        <p data-testid="discovery-confirmation-message">Your discovery brief is with our team. Expect a note from us within one business day.</p>
      </motion.div>
    </div>
  </main>;

  return <main id="main-content" className="container discovery-page" data-testid="discovery-page">
    <section className="page-hero" style={{ paddingBottom: 30 }}>
      <Reveal><Eyebrow id="discovery-hero-eyebrow" dot>INTAKE FORM</Eyebrow></Reveal>
      <AnimatedHeading lines={[{ text: 'Tell us where' }, { text: 'AI should start.', accent: true }]} id="discovery-hero-heading" className="page-title" />
      <Reveal delay={0.2}><p className="page-description" data-testid="discovery-hero-description">A short, structured brief on your processes, systems and goals. Eight steps — required fields marked with an asterisk, everything else optional.</p></Reveal>
    </section>

    <div className="discovery-shell">
      <div className="discovery-progress" data-testid="discovery-progress">
        {[...discoverySections.map(s => s.title), 'Review'].map((title, i) => (
          <button type="button" key={title} disabled={i > step} onClick={() => goto(i)} className={`discovery-step-pip ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`} data-testid={`discovery-step-pip-${i + 1}`}>
            <span className="pip-index">{i < step ? <Check size={11} /> : i + 1}</span>{title}
          </button>
        ))}
      </div>
      <div className="discovery-rail" aria-hidden="true"><span style={{ width: `${Math.max(progress, 6)}%` }} /></div>

      <div className="discovery-card glass" data-testid="discovery-card">
        {step < REVIEW ? <>
          <div className="discovery-card-head">
            <span className="eyebrow" data-testid="discovery-step-eyebrow"><span className="eyebrow-line" />STEP {step + 1} OF {REVIEW + 1}</span>
            <h2 data-testid="discovery-step-title">{section.title}</h2>
            <p data-testid="discovery-step-intro">{section.intro}</p>
          </div>
          <div className="discovery-fields">
            {section.fields.map(field => <DiscoveryField key={field.key} field={field} value={answers[field.key]} error={errors[field.key]} onChange={v => set(field.key, v)} />)}
          </div>
        </> : <>
          <div className="discovery-card-head">
            <span className="eyebrow" data-testid="discovery-step-eyebrow"><span className="eyebrow-line" />STEP {REVIEW + 1} OF {REVIEW + 1}</span>
            <h2 data-testid="discovery-step-title">Review your answers</h2>
            <p data-testid="discovery-step-intro">Everything you’ve told us, grouped by section. Edit anything before you send.</p>
          </div>
          <div data-testid="discovery-review">
            {discoverySections.map((group, i) => {
              const filled = group.fields.filter(f => (Array.isArray(answers[f.key]) ? answers[f.key].length : String(answers[f.key]).trim()));
              return <div className="review-group" key={group.id} data-testid={`discovery-review-${group.id}`}>
                <div className="review-group-head">
                  <h3>{group.title}</h3>
                  <button type="button" className="review-edit" onClick={() => goto(i)} data-testid={`discovery-review-edit-${group.id}`}>Edit</button>
                </div>
                {filled.length ? <dl className="review-rows">{filled.map(f => <div className="review-row" key={f.key} data-testid={`discovery-review-row-${f.key}`}>
                  <dt>{f.label}</dt><dd>{display(answers[f.key])}</dd>
                </div>)}</dl> : <p className="review-empty">Nothing added in this section.</p>}
              </div>;
            })}
          </div>
          {failure && <p className="discovery-error" data-testid="discovery-submit-error">{failure}</p>}
        </>}

        <div className="discovery-actions">
          {step > 0 && <Button type="button" className="action-button button-secondary" onClick={() => goto(step - 1)} data-testid="discovery-back"><ArrowLeft size={16} /> Back</Button>}
          {step < REVIEW
            ? <Button type="button" className="action-button button-primary" onClick={next} data-testid="discovery-next">Continue <ArrowRight size={17} /></Button>
            : <Button type="button" className="action-button button-primary" onClick={submit} disabled={sending} data-testid="discovery-submit">{sending ? 'Sending…' : <>Submit brief <Send size={16} /></>}</Button>}
          <span className="step-count" data-testid="discovery-step-count"><Sparkles size={12} style={{ display: 'inline', marginRight: 6 }} />{step + 1} / {REVIEW + 1}</span>
        </div>
        {step === REVIEW && <p className="form-privacy-note" data-testid="discovery-privacy-note">By submitting, you agree to our <Link to="/privacy-policy">Privacy Policy</Link>.</p>}
      </div>
    </div>
  </main>;
}
